import { versE164 } from '../phone/e164.js';
/**
 * Échappement RFC 6350 §3.4.
 *
 * ⚠️ L'ordre compte : la barre oblique inverse EN PREMIER, sinon on échappe
 * les barres qu'on vient d'ajouter, et « Dupont ; fils » ressort avec des
 * doublons qui apparaissent tels quels dans le répertoire.
 */
function echapper(valeur) {
    return valeur
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n');
}
/**
 * Pliage des lignes longues (RFC 6350 §3.2).
 *
 * ⚠️ Le pliage se compte en OCTETS, pas en caractères — et il ne doit jamais
 * couper une séquence UTF-8 en deux. « Aïssata » ou « Touré » suffisent à
 * produire l'accident : la ligne coupée au mauvais octet donne un caractère
 * de remplacement dans le répertoire, définitivement.
 */
function plier(ligne) {
    const encodeur = new TextEncoder();
    if (encodeur.encode(ligne).length <= 75)
        return ligne;
    const morceaux = [];
    let courant = '';
    let octets = 0;
    // La première ligne tient 75 octets, les suivantes 74 : l'espace de
    // continuation en consomme un.
    let limite = 75;
    for (const caractere of ligne) {
        const taille = encodeur.encode(caractere).length;
        if (octets + taille > limite) {
            morceaux.push(courant);
            courant = '';
            octets = 0;
            limite = 74;
        }
        courant += caractere;
        octets += taille;
    }
    if (courant)
        morceaux.push(courant);
    return morceaux.join('\r\n ');
}
function ligne(nom, valeur) {
    return plier(`${nom}:${valeur}`);
}
/**
 * Le numéro tel qu'il doit figurer dans le répertoire.
 *
 * ⭐ Au format international quand c'est possible : un contact enregistré en
 * « 699 00 01 11 » ne se compose plus dès qu'on quitte le pays, et ne
 * correspond à rien pour WhatsApp.
 *
 * ⚠️ Mais on GARDE le numéro brut quand la mise en forme échoue — contrairement
 * à un envoi, où un numéro douteux doit être écarté. Ici l'humain lit la fiche
 * et saura quoi en faire ; un contact sans numéro, lui, ne sert à personne.
 */
function telephone(brut, pays) {
    const propre = brut?.trim();
    if (!propre)
        return null;
    return versE164(propre, pays) ?? propre;
}
/** Une fiche, en vCard 3.0. */
export function versVCard(contact, options = {}) {
    const lignes = ['BEGIN:VCARD', 'VERSION:3.0'];
    const prefixe = options.prefixe?.trim();
    const affiche = prefixe ? `${prefixe} · ${contact.displayName}` : contact.displayName;
    lignes.push(ligne('FN', echapper(affiche)));
    /**
     * ⚠️ `N` est STRUCTURÉ et ses cinq champs sont obligatoires, même vides :
     * nom;prénom;autres;préfixe;suffixe. Un `N` incomplet fait rejeter la fiche
     * entière par le répertoire d'iOS, silencieusement.
     */
    // ⚠️ Toujours une valeur, jamais une chaîne vide : un `N` sans nom de famille
    // range le contact sous une entrée anonyme, en tête du répertoire. Une
    // société n'a pas de nom de famille — c'est sa raison sociale qui en tient
    // lieu.
    const nom = contact.lastName?.trim() || contact.companyName?.trim() || contact.displayName || 'Client';
    lignes.push(ligne('N', `${echapper(prefixe ? `${prefixe} · ${nom}` : nom)};${echapper(contact.firstName ?? '')};;;`));
    if (contact.companyName)
        lignes.push(ligne('ORG', echapper(contact.companyName)));
    // Le pays de la fiche d'abord, celui de l'enseigne à défaut.
    const paysDuNumero = contact.countryCode ?? options.pays;
    const mobile = telephone(contact.mobile, paysDuNumero);
    const fixe = telephone(contact.phone, paysDuNumero);
    if (mobile)
        lignes.push(ligne('TEL;TYPE=CELL', mobile));
    // Le même numéro dans les deux champs de la fiche ne doit pas produire deux
    // entrées identiques dans le répertoire.
    if (fixe && fixe !== mobile)
        lignes.push(ligne('TEL;TYPE=WORK,VOICE', fixe));
    if (contact.email)
        lignes.push(ligne('EMAIL;TYPE=INTERNET', echapper(contact.email)));
    const rue = [contact.addressLine1, contact.addressLine2].filter(Boolean).join(', ');
    if (rue || contact.city || contact.postalCode) {
        // boîte;complément;rue;ville;région;code postal;pays
        lignes.push(ligne('ADR;TYPE=HOME', `;;${echapper(rue)};${echapper(contact.city ?? '')};;${echapper(contact.postalCode ?? '')};${echapper(contact.countryCode ?? '')}`));
    }
    const notes = [
        contact.code ? `Référence ${contact.code}` : null,
        prefixe ? `Client de ${prefixe}` : null,
    ].filter(Boolean);
    if (notes.length > 0)
        lignes.push(ligne('NOTE', echapper(notes.join(' — '))));
    /**
     * ⭐ `UID` stable : l'identifiant de la fiche, à l'identique d'un export à
     * l'autre. Les répertoires qui l'exploitent — iOS le fait en partie —
     * METTENT À JOUR le contact au lieu d'en créer un second. Ceux qui l'ignorent
     * ne s'en portent pas plus mal.
     */
    lignes.push(ligne('UID', `urn:uuid:${contact.id}`));
    lignes.push('END:VCARD');
    return lignes.join('\r\n');
}
/**
 * Le carnet complet, prêt à être ouvert par le téléphone.
 *
 * ⚠️ Terminaisons CRLF de bout en bout, y compris la dernière : le format les
 * impose, et le répertoire d'iOS refuse un fichier qui s'en écarte — sans
 * afficher la moindre erreur, ce qui laisse croire que l'export a échoué.
 */
export function versCarnet(contacts, options = {}) {
    return contacts.map((c) => versVCard(c, options)).join('\r\n') + '\r\n';
}
/** A-t-on de quoi enregistrer un contact utile ? Sans numéro ni courriel, non. */
export function contactExportable(c) {
    return Boolean(c.mobile?.trim() || c.phone?.trim() || c.email?.trim());
}
/** Nom de fichier lisible dans le gestionnaire de téléchargements. */
export function nomDuCarnet(boutique) {
    const propre = boutique
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();
    return `contacts-${propre || 'boutique'}.vcf`;
}
//# sourceMappingURL=vcard.js.map